import { IRREGULAR_PRONUNCIATIONS } from "@/src/lib/name-matching/pronunciation-exceptions";

const LETTER_VOWELS = "aeiouāēīōūë";

export function isLetterVowel(letter: string | undefined): boolean {
  return letter !== undefined && LETTER_VOWELS.includes(letter);
}

type PronunciationRule = {
  pattern: RegExp;
  phonemes: (context: {
    spelling: string;
    atStart: boolean;
    atEnd: boolean;
    next: string | undefined;
    // Whether a vowel letter precedes the match, i.e. the match is not in the first syllable.
    afterVowel: boolean;
    // Whether the match follows a doubled consonant (Huggins: the second g stays hard).
    afterDoubled: boolean;
  }) => string[][];
};

// First matching rule wins. Sticky patterns match only at the current position; each rule returns
// the alternative sounds for its spelling, including an empty sequence for silent letters.
// Lowercase consonants use x for sh, c for ch, and 0 for th. Uppercase tokens represent
// vowels; @ is schwa and E! is a stressed e. These are spelling approximations, not IPA.
const RULES: readonly PronunciationRule[] = [
  // Vowel spellings
  { pattern: /eigh/y, phonemes: ({ atEnd }) => [[atEnd ? "EE" : "AY"]] },
  { pattern: /igh/y, phonemes: () => [["IE"]] },
  // ough is OH only at the end (Dough); Coughlin and Doughty are not Colon and Doty.
  { pattern: /ough/y, phonemes: ({ atEnd }) => (atEnd ? [["AW"], ["OO"], ["OW"], ["OH"]] : [["AW"], ["OO"], ["OW"]]) },
  { pattern: /ugh/y, phonemes: () => [["OO"]] },
  { pattern: /augh/y, phonemes: () => [["AW"]] },
  { pattern: /eye/y, phonemes: () => [["IE", "@"]] },
  // Final ey is EE after another syllable (Kelsey) and AY on its own (Rey, Grey); Keys is KEEZ.
  { pattern: /ey(?=s$)/y, phonemes: () => [["EE"]] },
  { pattern: /ey/y, phonemes: ({ atEnd, afterVowel }) => [[atEnd && afterVowel ? "EE" : "AY"]] },
  // Unstressed final -say and -lay sound like -sey and -ley (Lindsay, Barclay); McKay keeps AY.
  { pattern: /(?<=[sl])ay(?![a-z])/y, phonemes: ({ afterVowel }) => (afterVowel ? [["AY"], ["EE"]] : [["AY"]]) },
  { pattern: /[ai]y/y, phonemes: () => [["AY"]] },
  // A final ai is the vowel in "eye" (Lai, Mai, Rai, Kai). Aileen and Eileen are both EYE-leen;
  // elsewhere ai is AY (Haines, Tailor).
  { pattern: /ai(?![a-z])/y, phonemes: () => [["IE"]] },
  { pattern: /ai/y, phonemes: ({ atStart }) => (atStart ? [["AY"], ["IE"]] : [["AY"]]) },
  // ae is AY (Kraemer, Baek); before l or f it may be short (Schaefer is SHAF-er, Rachael is RAY-chul).
  { pattern: /ae(?=[lf])/y, phonemes: () => [["AY"], ["A"]] },
  // Spanish a-e is two syllables (Paez, Baez), unlike Pace and Bays.
  { pattern: /ae(?=z)/y, phonemes: () => [["A", "E"]] },
  { pattern: /ae/y, phonemes: () => [["AY"]] },
  // Neil, Stein; ei before n may be AY (Reina), and a final ei is AY as in pinyin (Lei, Wei, Mei).
  {
    pattern: /ei/y,
    phonemes: ({ atEnd, next }) => (atEnd ? [["AY"]] : next === "n" ? [["EE"], ["IE"], ["AY"]] : [["EE"], ["IE"]]),
  },
  // A final ie that is the only vowel is the pinyin -ie of Jie or Xie (yeh) or the English vowel of Pie.
  { pattern: /ie(?![a-z])/y, phonemes: ({ afterVowel }) => (afterVowel ? [["EE"]] : [["IE"], ["EE", "E"]]) },
  { pattern: /ie/y, phonemes: () => [["EE"], ["IE"]] },
  { pattern: /[iy]ah?(?![a-z])|[iy]a/y, phonemes: () => [["IA"]] },
  // A two-syllable ea sounds like ia (Leah/Lia, Breanna/Brianna, Andrea/Andria).
  { pattern: /eah(?![a-z])/y, phonemes: () => [["IA"]] },
  // Final ea is two syllables after another syllable (Andrea, Dorothea, Correa); Lea may be either.
  { pattern: /ea(?![a-z])/y, phonemes: ({ afterVowel }) => (afterVowel ? [["IA"]] : [["EE"], ["IA"]]) },
  // Deanna is dee-AN-a, not Dena.
  { pattern: /ea(?=nna)/y, phonemes: () => [["IA"]] },
  // Initial ear is ER (Earnest/Ernest, Earl); elsewhere EER (Pearce/Pierce, Pearson is not Person).
  { pattern: /ea(?=r[^aeiouy])/y, phonemes: ({ atStart }) => (atStart ? [["EE"], ["E"]] : [["EE"]]) },
  // An unstressed ea before another syllable can reduce (Eleanor is EL-uh-nor; Andreas is not).
  { pattern: /ea(?=[^aeiouy]+[aeiouy])/y, phonemes: ({ afterVowel }) => (afterVowel ? [["EE"], ["@"]] : [["EE"]]) },
  { pattern: /ee|ea/y, phonemes: () => [["EE"]] },
  { pattern: /eo/y, phonemes: () => [["EO"]] },
  { pattern: /oey(?![a-z])/y, phonemes: () => [["OH", "EE"]] },
  // Final oe is two syllables after z or l (Zoe, Chloe) and one elsewhere (Joe, Monroe).
  { pattern: /(?<=[zl])oe(?![a-z])/y, phonemes: () => [["OH"], ["OH", "EE"]] },
  { pattern: /oe(?![a-z])/y, phonemes: () => [["OH"]] },
  // Final oa is two syllables (Roa, Noa), unlike Rowe.
  { pattern: /oa(?![a-z])/y, phonemes: () => [["OH", "A"]] },
  { pattern: /oa/y, phonemes: () => [["OH"]] },
  { pattern: /ow/y, phonemes: () => [["OW"], ["OH"]] },
  // ou before a consonant is OW (House, Couch, Houck); at the end or before a vowel it is OO (Lou, Louis).
  { pattern: /ou(?=r)/y, phonemes: () => [["OW"], ["O"]] },
  { pattern: /ou(?=[^aeiouy])/y, phonemes: () => [["OW"]] },
  { pattern: /ou/y, phonemes: () => [["OO"]] },
  // ue is OO (Sue, Krueger) except before l, where both vowels sound (Luella, Samuel).
  { pattern: /oo|ew|ue(?!l)/y, phonemes: () => [["OO"]] },
  { pattern: /au|aw/y, phonemes: () => [["AW"]] },
  { pattern: /o[iy]/y, phonemes: () => [["OY"]] },
  { pattern: /ā/y, phonemes: () => [["AY"]] },
  { pattern: /ē/y, phonemes: () => [["EE"]] },
  { pattern: /ī/y, phonemes: () => [["IE"]] },
  { pattern: /ō/y, phonemes: () => [["OH"]] },
  { pattern: /ū/y, phonemes: () => [["OO"]] },
  { pattern: /é/y, phonemes: () => [["E!"]] },
  { pattern: /u(?=[aeio])/y, phonemes: () => [["OO"]] },
  { pattern: /i(?![a-z])/y, phonemes: () => [["EE"]] },
  { pattern: /y(?=[eou])/y, phonemes: ({ atStart }) => [atStart ? ["y"] : ["IE"]] },
  {
    pattern: /y/y,
    phonemes: ({ atStart, next, atEnd }) => (atEnd ? [["EE"]] : atStart && isLetterVowel(next) ? [["y"]] : [["I"]]),
  },
  // The e of a final -es or -ez is a full vowel in Spanish and Portuguese names (Lopes/Lopez, Reyes)
  // and silent in English ones (Brookes, Wiles): Wiles is never Willis, nor Ames Amos.
  { pattern: /ë/y, phonemes: () => [["E!"], []] },
  { pattern: /ə/y, phonemes: () => [["@"]] },
  { pattern: /ǧ/y, phonemes: () => [["j"], ["g"]] },
  { pattern: /(?<=[^aeiouy])e(?=z$)/y, phonemes: () => [["E!"]] },
  { pattern: /[aeiou]/y, phonemes: ({ spelling }) => [[spelling.toUpperCase()]] },

  // Consonant spellings
  // Initial wa before t, l, s, n or d is AW (Waters, Walsh, Wanda), never AY.
  { pattern: /wa(?=[tlsndh])/y, phonemes: ({ atStart }) => (atStart ? [["w", "AW"]] : [["w", "A"]]) },
  { pattern: /tch/y, phonemes: () => [["t", "c"]] },
  { pattern: /sch|sh/y, phonemes: () => [["x"]] },
  { pattern: /ch(?=[rl])/y, phonemes: () => [["k"]] },
  // French-derived initial ch is sh (Cherie, Charlene, Chantal); otherwise ch (Chen, Chad).
  { pattern: /ch(?=ar|er|ant|anel)/y, phonemes: ({ atStart }) => (atStart ? [["c"], ["x"]] : [["k"], ["c"]]) },
  // A final ch after a consonant is ch (Burch, Finch, Lynch), not k.
  { pattern: /(?<=[^aeiouy])ch(?![a-z])/y, phonemes: () => [["c"]] },
  { pattern: /ch/y, phonemes: ({ atStart }) => (atStart ? [["c"]] : [["k"], ["c"]]) },
  // Pinyin zh is a j sound (Zhou, Zhang), distinct from z (Zeng, Zi).
  { pattern: /zh/y, phonemes: () => [["j"]] },
  { pattern: /ph/y, phonemes: ({ atStart }) => (atStart ? [["f"]] : [["f"], ["v"]]) },
  // th may be t only at the start (Theresa, Thomas, Thompson); Kathy is not Katie.
  { pattern: /th/y, phonemes: ({ atStart }) => (atStart ? [["0"], ["t"]] : [["0"]]) },
  // gh is a g after n (Singh) or before a vowel (Meghan).
  { pattern: /(?<=n)gh|gh(?=[aeiouy])/y, phonemes: () => [["g"]] },
  { pattern: /gh/y, phonemes: ({ atStart }) => (atStart ? [["g"]] : [[]]) },
  { pattern: /wh/y, phonemes: () => [["w"]] },
  { pattern: /mp(?=[st])/y, phonemes: () => [["m", "p"], ["m"]] },
  // Marcia/Marsha, Alicia/Alisha.
  {
    pattern: /cia/y,
    phonemes: ({ atStart }) =>
      atStart
        ? [["s", "IA"]]
        : [
            ["s", "IA"],
            ["x", "A"],
          ],
  },
  { pattern: /c?qu(?=[aeiouy])/y, phonemes: () => [["k", "w"]] },
  // Spanish gu is gw before a or o (Guadalupe, Guan) and a hard g before e or i (Miguel, Guillermo).
  { pattern: /gu(?=[ao])/y, phonemes: () => [["g", "w"]] },
  { pattern: /gu(?=[ei])/y, phonemes: () => [["g"]] },
  { pattern: /ck|cq|cc(?![eiy])|q/y, phonemes: () => [["k"]] },
  { pattern: /cc(?=[eiy])/y, phonemes: () => [["k", "s"]] },
  { pattern: /c(?=[eiyēī])/y, phonemes: () => [["s"]] },
  { pattern: /c/y, phonemes: () => [["k"]] },
  { pattern: /dg/y, phonemes: () => [["j"]] },
  { pattern: /g(?=[eiyēī])/y, phonemes: ({ afterDoubled }) => (afterDoubled ? [["g"]] : [["j"], ["g"]]) },
  // Initial x is sh in pinyin before i or u (Xu, Xue, Xiao) and z otherwise (Xavier, Xena).
  {
    pattern: /x/y,
    phonemes: ({ atStart, next }) => [atStart ? (next === "i" || next === "u" ? ["x"] : ["z"]) : ["k", "s"]],
  },
  { pattern: /z(?![a-z])/y, phonemes: () => [["s"]] },
  // Spanish z is s before a consonant and in -eza/-oza (Vazquez, Meza, Espinoza); English names keep
  // z (Suzanne, Eliza).
  { pattern: /z(?=[^aeiouy]|[eo]?za$)|(?<=[eo])z(?=a$)/y, phonemes: () => [["z"], ["s"]] },
  { pattern: /h/y, phonemes: ({ atStart }) => [atStart ? ["h"] : []] },
  { pattern: /w/y, phonemes: ({ next }) => [isLetterVowel(next) || next === "y" ? ["w"] : []] },
  { pattern: /[bdfgjklmnprstvz]/y, phonemes: ({ spelling }) => [[spelling]] },
];

const LONG_VOWEL_LETTERS: ReadonlyMap<string, string> = new Map([
  ["a", "ā"],
  ["e", "ē"],
  ["i", "ī"],
  ["o", "ō"],
  ["u", "ū"],
  ["y", "ī"],
]);
const SOFT_CONSONANTS: ReadonlyMap<string, string> = new Map([
  ["c", "s"],
  ["g", "j"],
]);

/** Rewrites applied to the whole token before scanning. */
export function respellNameToken(token: string): string {
  return (
    (IRREGULAR_PRONUNCIATIONS.get(token) ?? token)
      .replace(/^(kn|gn)/, "n")
      .replace(/^wr/, "r")
      .replace(/^ps/, "s")
      // Mc and Mac are one unstressed prefix (McKenzie, MacKenzie), so McMillan is not McMullen.
      .replace(/^ma?c(?=[bcdfgjklmnpqrstvwz][a-z]{2})/, "mək")
      .replace(/dt$/, "t")
      // Final e after y is silent (Faye, Kaye), as is the e of -es after a vowel sound (Hayes, Hughes).
      .replace(/(?<=[aeiou])ye$/, "y")
      // Japanese ai is the vowel in "eye" (Saito, Kaito), so these stay apart from Sato and Kato.
      .replace(/ai(?=[^aeiouy]+[iou]$)/, "ī")
      .replace(/(?<=ay|gh|i)es$/, "s")
      // The e of a final -es is a full vowel in Spanish and Portuguese names (Lopes/Lopez) and silent in
      // English ones (Brookes); silent, it lengthens the vowel of a one-syllable name (Wiles is not
      // Wills or Willis, Cates is not Katz).
      .replace(
        /^([^aeiouy]*)([aeiouy])([bcdfgklmnprstvz])es$/,
        (_, onset: string, vowel: string, consonant: string) => {
          const long = LONG_VOWEL_LETTERS.get(vowel) ?? vowel;

          return `${onset}${long}${consonant}ës`;
        }
      )
      .replace(/(?<=[^aeiouyë])es$/, "ës")
      // Louise is loo-EEZ, not Louis.
      .replace(/(?<=[aeiou])ise$/, "ēs")
      .replace(/aughan$/, "aughn")
      // Unstressed -ace and -ice are both -iss (Candace/Candice, Janice/Janis).
      .replace(/(?<=[aeiouy][^aeiouy]+)[ai]ce$/, "is")
      // French stressed endings: Danielle is dan-YELL, not DAN-yel.
      .replace(/e(ll|tt)e$/, "é$1")
      // Silent-e lengthening (Jane, Clare, Stine) applies to one vowel letter before one consonant.
      .replace(/(^|[^aeiouy])([aeiou])([bcdfgklmnprstvz])e$/, (_, before: string, vowel: string, consonant: string) => {
        const long = LONG_VOWEL_LETTERS.get(vowel) ?? vowel;

        // The dropped e kept c and g soft (Jace, Paige); spell the sound so the scan cannot harden it.
        return `${before}${long}${SOFT_CONSONANTS.get(consonant) ?? consonant}`;
      })
      // Remaining final e after a consonant is silent (Anne, Gayle, Claire), but it kept c and g soft
      // (France, George).
      .replace(/ce$/, "s")
      // Lange may be Lang.
      .replace(/(?<=n)ge$/, "ǧ")
      .replace(/ge$/, "j")
      .replace(/([^aeiouy])e$/, "$1")
  );
}

/** Read one spelling once, preserving rule priority and the context of doubled consonants. */
export function readPronunciation(
  word: string,
  index: number,
  afterDoubled: boolean
): { spelling: string; alternatives: string[][] } | null {
  for (const rule of RULES) {
    rule.pattern.lastIndex = index;
    const match = rule.pattern.exec(word);

    if (!match) continue;

    const spelling = match[0];
    const next = word[index + spelling.length];

    return {
      spelling,
      alternatives: rule.phonemes({
        spelling,
        atStart: index === 0,
        atEnd: next === undefined,
        next,
        afterVowel: /[aeiouyāēīōū]/.test(word.slice(0, index)),
        afterDoubled,
      }),
    };
  }

  return null;
}
